// API keys and the fine-grained token scope vocabulary (`06` §3.2,
// `SC-F203`–`SC-F212`).
//
// Three things the key-creation UI must get right, all of them contract:
//
//   - **The plaintext appears exactly once**, in the create response, and is
//     never retrievable again. A screen that loses it has cost the user a key.
//   - **Creation requires an `Idempotency-Key`** (`req`, not `opt`). The
//     response reveals the secret once; a timed-out retry that mints a *second*
//     key is a silent credential leak.
//   - **Creation requires a step-up grant** even for a general key, and grants
//     are minted only on session routes. A general key can therefore list and
//     revoke unattended but cannot mint new authority by itself. That asymmetry
//     is the point: rotation is scriptable up to where new authority is created,
//     and no further.
//
// `expires_in_seconds: null` means unlimited and must be confirmed in the UI
// **before** verification is spent, so the five-minute grant stays fresh through
// the confirm dialog (`SC-S202`).

import { del, getJSON, newIdempotencyKey, sendJSON, type Page } from "./http.js";

const BASE = "/api/cloud/auth/api-keys";

/**
 * Feature is one grantable slice of the `/v1` surface for a fine-grained token
 * (`SC-F205`). The vocabulary is **coarse by design** and must match
 * `internal/access`'s exactly — the server answers `422 validation_failed` for
 * anything else, asserted by a test against the Go vocabulary.
 *
 * There is deliberately no feature for security management: no token can manage
 * passkeys, mint a step-up grant, or reach the playground. That is what closes
 * the leaked-key → exchange → mint-more-keys loop (`SC-S206`).
 */
export type Feature =
  | "instances"
  | "policies"
  | "datasources"
  | "validation"
  | "audit"
  | "analytics"
  | "members"
  | "invites"
  | "billing";

/** FEATURES is the vocabulary in canonical order with the copy for the
 * permission grid on the create form. The hints paraphrase `01` §6.1. */
export const FEATURES: ReadonlyArray<{ id: Feature; label: string; hint: string }> = [
  {
    id: "instances",
    label: "Instances",
    hint: "Instance CRUD, power operations, status, logs, sizing, upgrade, secret inventory",
  },
  {
    id: "policies",
    label: "Policies",
    hint: "All 11 policy kinds, drafts, generations, publish, rollback, server.yaml",
  },
  {
    id: "datasources",
    label: "Datasources",
    hint: "Datasource CRUD, credential capture, schema introspection",
  },
  {
    id: "validation",
    label: "Validation",
    hint: "The proxied CLI surface: validate, explain, test, datasource check, audit verify",
  },
  {
    id: "audit",
    label: "Audit",
    hint: "Audit read, search, export, chain verification, organization activity feed",
  },
  {
    id: "analytics",
    label: "Analytics",
    hint: "Query analytics, instance metrics, alert rules, webhook subscriptions",
  },
  { id: "members", label: "Members", hint: "Organization membership, roles and settings" },
  { id: "invites", label: "Invitations", hint: "Invitation create, list and revoke" },
  {
    id: "billing",
    label: "Billing",
    hint: "Plan, quota, usage and retention reads. Never a plan or payment change",
  },
];

/** KeyKind separates full-authority general keys from narrowed fine-grained
 * tokens. The wire prefixes are `sluice_ak_` and `sluice_ft_`. */
export type KeyKind = "general" | "fine_grained";

/**
 * KeyScope narrows a fine-grained token to one organization, optionally one
 * instance within it, and a feature set. `SC-F206` narrows organization →
 * instance and **stops** — there is no third level, because a third would be
 * authority the underlying product cannot express (`06` §3.5).
 */
export interface KeyScope {
  /** Organization record id. Empty or absent = all the owner's organizations. */
  org?: string;
  /** Instance record id. Requires `org`. Empty or absent = the whole organization. */
  instance?: string;
  features: Feature[];
}

/** ApiKey is one key as it appears in a listing. The secret is never here. */
export interface ApiKey {
  id: string;
  name: string;
  kind: KeyKind;
  /** The full display prefix, e.g. `sluice_ft_9fK2xQ7p` — prefix plus the first
   * 8 random characters, which is all that is retained of the plaintext. */
  display_prefix: string;
  created: string;
  /** Empty string when never used. */
  last_used: string;
  /** Null when the key never expires. */
  expires: string | null;
  /** Present on fine-grained tokens only. */
  scope?: KeyScope;
}

/** ApiKeyCreated is the create response — the one and only time `secret` exists
 * outside the caller's own storage. */
export interface ApiKeyCreated extends ApiKey {
  secret: string;
}

/** CreateKeyInput is the create body (`06` §3.2). */
export interface CreateKeyInput {
  name: string;
  kind: KeyKind;
  /** Lifetime in seconds. **null means unlimited** — confirm that in the UI
   * before spending the verification grant, not after. */
  expires_in_seconds: number | null;
  /** Required with at least one feature when `kind` is `fine_grained`. */
  scope?: KeyScope;
}

/** listApiKeys returns the caller's keys, **including expired rows**
 * (`SC-F210`) — an expired key that silently vanished would look revoked. */
export function listApiKeys(): Promise<Page<ApiKey>> {
  return getJSON<Page<ApiKey>>(BASE);
}

/**
 * createApiKey mints a key and returns the plaintext exactly once.
 *
 * `verification` is a single-use step-up grant for `create_api_key`, obtained
 * from `confirmStepUpOtp` or `verifyWithPasskey`. The idempotency key is minted
 * here rather than by the caller because the route requires one and there is no
 * legitimate reason for a caller to choose it.
 */
export function createApiKey(
  input: CreateKeyInput,
  opts: { verification: string },
): Promise<ApiKeyCreated> {
  return sendJSON<ApiKeyCreated>(BASE, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
    verification: opts.verification,
  });
}

/** revokeApiKey deletes a key. Revocation is effective on the **next** request
 * (`SC-F210`): there is no session to tear down, only a hash to stop matching. */
export function revokeApiKey(id: string): Promise<void> {
  return del(`${BASE}/${encodeURIComponent(id)}`);
}

/** ExchangeResult is a short-lived JWT minted from an opaque key (`SC-F209`, v1)
 * — the CLI's entry point, anonymous by design. */
export interface ExchangeResult {
  token: string;
  expires: string;
  key_id: string;
}

/** exchangeApiKey swaps a plaintext key for a 15-minute JWT. Anonymous: the key
 * itself is the credential, so no bearer token is attached. */
export function exchangeApiKey(secret: string): Promise<ExchangeResult> {
  return sendJSON<ExchangeResult>(
    `${BASE}/exchange`,
    "POST",
    { token: secret },
    { anonymous: true },
  );
}
